/**
 * ============================================================================
 * [P3-04-B] URLLinkTab - URL 연결 탭 메인 컴포넌트
 *
 * 기능:
 * - Firebase 실시간 URL 목록 표시
 * - 드래그 앤 드롭 정렬
 * - URL 추가/수정/삭제
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useState, useCallback } from 'react'
import { URLList } from './URLList'
import { AddURLForm } from './AddURLForm'
import { useURLs } from '@/hooks/useURLs'
import type { URLLink } from '@/types'
import styles from './URLLink.module.css'

// ============================================================================
// 2. URLLinkTab Component
// ============================================================================
export function URLLinkTab() {
  // ========================================================================
  // 2-1. useURLs 훅으로 Firebase 연동
  // ========================================================================
  const { urls, isLoading, error, addURL, updateURL, removeURL, reorderURLs } = useURLs()

  // ========================================================================
  // 2-2. 폼 상태
  // ========================================================================
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingUrl, setEditingUrl] = useState<URLLink | null>(null)

  // ========================================================================
  // 2-3. URL 추가/수정 핸들러
  // ========================================================================
  const handleSubmitURL = useCallback(async (urlData: Omit<URLLink, 'id' | 'createdAt'>) => {
    if (editingUrl) {
      // 수정 모드
      const success = await updateURL(editingUrl.id, urlData)
      if (success) {
        setShowAddForm(false)
        setEditingUrl(null)
      }
    } else {
      // 추가 모드
      const newUrl = await addURL(urlData)
      if (newUrl) {
        setShowAddForm(false)
      }
    }
  }, [editingUrl, updateURL, addURL])

  // ========================================================================
  // 2-4. URL 수정 폼 열기 핸들러
  // ========================================================================
  const handleEditURL = useCallback((id: string) => {
    const urlToEdit = urls.find((u) => u.id === id)
    if (urlToEdit) {
      setEditingUrl(urlToEdit)
      setShowAddForm(true)
    }
  }, [urls])

  // ========================================================================
  // 2-5. URL 삭제 핸들러
  // ========================================================================
  const handleDeleteURL = useCallback(async (id: string) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      await removeURL(id)
    }
  }, [removeURL])

  // ========================================================================
  // 2-6. 폼 닫기 핸들러
  // ========================================================================
  const handleCloseForm = useCallback(() => {
    setShowAddForm(false)
    setEditingUrl(null)
  }, [])

  // ========================================================================
  // 2-7. Render
  // ========================================================================
  return (
    <div className={styles.urlLinkTab}>
      {/* ================================================================
          헤더 영역
          ================================================================ */}
      <div className={styles.tabHeader}>
        <h3>자주 사용하는 링크</h3>
        <button
          className={styles.addBtn}
          onClick={() => setShowAddForm(true)}
        >
          + 링크 추가
        </button>
      </div>

      {/* ================================================================
          로딩/에러 상태
          ================================================================ */}
      {isLoading && (
        <div className={styles.emptyState}>
          <p>로딩 중...</p>
        </div>
      )}

      {error && (
        <div className={styles.emptyState}>
          <p>오류가 발생했습니다: {error.message}</p>
        </div>
      )}

      {/* ================================================================
          URL 목록 (드래그 앤 드롭 지원)
          ================================================================ */}
      {!isLoading && !error && (
        <URLList
          urls={urls}
          onReorder={reorderURLs}
          onEdit={handleEditURL}
          onDelete={handleDeleteURL}
        />
      )}

      {/* ================================================================
          추가/수정 폼 모달
          ================================================================ */}
      {showAddForm && (
        <AddURLForm
          initialData={editingUrl}
          onSubmit={handleSubmitURL}
          onClose={handleCloseForm}
        />
      )}
    </div>
  )
}

export default URLLinkTab

